import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { toastMessage } from "../../../constants/strings";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import {
  osadAssignmentSchema,
  osadShipmentsSchema,
  type OsadAssignment,
} from "../../../types/operations/osad.type";
import { http } from "../../../utils/httpCommon";
import { toast } from "../../../utils/snackbarHelper";
import { safeParsePartial } from "../../../utils/zodSafeParse";
import { useApiUrls } from "../../useApiUrl";

interface UseOSADArgs {
  sicId: number;
  onSuccess: (successInfo: {
    affectedShipmentsCount: number;
    shipmentIds: number[];
  }) => Promise<void> | void;
  onSettled?: () => void;
}

export const useOSAD = ({ sicId, onSuccess, onSettled }: UseOSADArgs) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const { createOSAD } = hookUrls;

  return useMutation({
    mutationFn: async (request: OsadAssignment) => {
      const payload = osadAssignmentSchema.parse(request);

      const url = createOSAD(sicId);
      const { status } = await http.post(url, payload);
      return status;
    },
    onSuccess: (_, variables) => {
      const uniqueShipmentIds = new Set(variables.shipmentIds);

      if (variables.shipmentIds.length > 0) {
        const affectedShipmentsCount = uniqueShipmentIds.size;

        onSuccess({
          affectedShipmentsCount,
          shipmentIds: variables.shipmentIds,
        });
      }
    },
    onError: (error: AxiosError) => {
      if (error.response) {
        toast(error.response.data?.toString(), { variant: "error" });
      } else {
        toast(toastMessage.generics.error, { variant: "error" });
      }
    },
    onSettled: () => {
      // Invalidating all queries because the list was long and complex: https://github.com/daylighttransport/operations-iol-webapp/pull/1370/files
      queryClient.invalidateQueries();
      onSettled?.();
    },
  });
};

export function useOSADShipments(sicId: number, enabled = true) {
  const hookUrls = useApiUrls();
  const { getOSADShipments } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useQuery({
    refetchOnMount: false,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["osad", "shipments", sicId],
    queryFn: async () => {
      const url = getOSADShipments(sicId);
      const { data } = await http.get(url);
      return isSafeParseEnabled
        ? safeParsePartial(osadShipmentsSchema, data, "useOSADShipments")
        : osadShipmentsSchema.parse(data);
    },
    enabled,
  });
}
